import _ from 'lodash';
import { Mic, NotebookIcon, SlashIcon } from 'lucide-react';
import { observer } from 'mobx-react-lite';
import { useEffect, useRef } from 'react';
import { twMerge } from 'tailwind-merge';
import LearningNote from './LearningNote';
import { useSentenceStore } from './provider/SentenceProvider';
import VocalGuide from './VocalGuide';
import React from 'react';

const ChunkSkeleton = ({ className }: { className: string }) => {
    return (
        <div
            // 한글 자막 영역을 패딩을 확보
            className="pb-6"
        >
            <div
                className={twMerge('skeleton border-base-300 h-11 rounded-lg border-2', className)}
            ></div>
        </div>
    );
};

const SentenceLearning = observer((): React.ReactNode => {
    const sentenceStore = useSentenceStore();
    const sentence = sentenceStore.sentence;

    const sentenceLearningRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        // 문장이 잘 보이게 스크롤
        if (sentenceLearningRef.current) {
            sentenceLearningRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [sentence, sentenceLearningRef]);

    return (
        <section className="min-w-[80%]" ref={sentenceLearningRef}>
            <div className="flex flex-wrap gap-x-2 gap-y-12 py-10">
                {_.isEmpty(sentence) && (
                    <>
                        <ChunkSkeleton className="w-70" />
                        <ChunkSkeleton className="w-40" />
                        <ChunkSkeleton className="w-50" />
                    </>
                )}
                {_.map(sentence?.chunks).map((chunk, index) => {
                    const isGuideOpen = sentenceStore.isVocalGuideOpen(index);
                    const hasVocalGuide = chunk.vocalGuide || chunk.learningNote;

                    return (
                        <React.Fragment key={index}>
                            {chunk.isJoined && (
                                <div className="flex h-full items-center justify-center">
                                    <div className="text-base-content/30 flex h-11 -rotate-20 items-center justify-center">
                                        <SlashIcon size={16} />
                                    </div>
                                </div>
                            )}
                            <div className="flex flex-col">
                                {/* 청크 박스 (relative for absolute positioning) */}
                                <div className="relative">
                                    {/* 가이드 아이콘 버튼 (텍스트 보일 때만) */}
                                    {sentenceStore.textVisibility === 'visible' && (
                                        <div
                                            className={twMerge(
                                                'absolute -top-5.5 left-1/2 z-10 h-6 w-full -translate-x-1/2',
                                                'flex flex-row items-center justify-center gap-0',
                                            )}
                                        >
                                            {hasVocalGuide && (
                                                <button
                                                    className={twMerge(
                                                        'btn btn-xs btn-circle btn-ghost relative overflow-visible',
                                                        sentenceStore.openedVocalGuideIndex ===
                                                            index && 'text-primary',
                                                        sentenceStore.openedVocalGuideIndex !==
                                                            index && 'text-base-content/60',
                                                    )}
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        sentenceStore.toggleVocalGuide(index);
                                                    }}
                                                >
                                                    <Mic size={12} />
                                                </button>
                                            )}
                                            {chunk.learningNote && (
                                                <button
                                                    className={twMerge(
                                                        'btn btn-xs btn-circle btn-ghost text-base-content/60 z-10',
                                                    )}
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        sentenceStore.setCurrentLearningNoteChunkIndex(
                                                            index,
                                                        );
                                                    }}
                                                >
                                                    <NotebookIcon size={12} />
                                                </button>
                                            )}
                                        </div>
                                    )}

                                    {/* 청크 텍스트 */}
                                    <div
                                        data-sentence-chunk-index={index}
                                        className={twMerge(
                                            'border-base-300 bg-base-100 hover:bg-base-200 group flex h-11 cursor-pointer items-center justify-center rounded-lg border-2 px-3 transition-all',
                                            // 학습 노트 하이라이트
                                            sentenceStore.currentLearningNoteChunkIndex === index &&
                                                'border-primary/60 bg-primary/5',
                                        )}
                                        onClick={() => {
                                            if (sentenceStore.textVisibility === 'hidden') {
                                                sentenceStore.setTextVisibility('visible');
                                            } else {
                                                sentenceStore.setTextVisibility('hidden');
                                            }
                                        }}
                                    >
                                        <div
                                            className={twMerge(
                                                'font-medium transition-all select-none',
                                                sentenceStore.textVisibility === 'hidden' &&
                                                    'blur-sm group-hover:blur-[3px]', // 청크에 마우스 오버했을 때는 해당 청크만 히트로 보여줌
                                                sentenceStore.textVisibility === 'hint' &&
                                                    'blur-[3px]',
                                            )}
                                        >
                                            {chunk.text}
                                        </div>
                                    </div>
                                </div>

                                {/* 청크 번역 또는 보컬 가이드 */}
                                {sentenceStore.openedVocalGuideIndex === index && (
                                    <div
                                        className={twMerge(
                                            'text-primary bg-base-100 flex min-h-6 cursor-pointer items-center text-xs',
                                        )}
                                        onClick={() => {
                                            sentenceStore.toggleVocalGuide(index);
                                        }}
                                    >
                                        <VocalGuide text={chunk.vocalGuide} />
                                    </div>
                                )}
                                {sentenceStore.openedVocalGuideIndex !== index && chunk.textKo && (
                                    <div
                                        className={twMerge(
                                            'flex min-h-6 items-center text-xs transition-opacity',
                                            !sentenceStore.isTranslationVisible && 'opacity-0',
                                            sentenceStore.isTranslationVisible && 'opacity-70',
                                        )}
                                    >
                                        {chunk.textKo}
                                    </div>
                                )}
                            </div>
                        </React.Fragment>
                    );
                })}
            </div>

            {/* 번역보기 버튼 및 전체 문장 번역 */}
            <div className="mt-4 min-h-6">
                <div
                    className={`text-sm transition-opacity ${
                        sentenceStore.isTranslationVisible ? 'opacity-70' : 'opacity-0'
                    }`}
                >
                    {sentence?.textKo}
                </div>
            </div>

            {/* 학습 노트 */}
            <LearningNote />
        </section>
    );
});

export default SentenceLearning;
